// Shared form primitives used across every LoanFileDetailPage module.
// Extracted verbatim from the original monolithic LoanFileDetailPage.jsx to keep
// behavior identical after the refactor.
import React from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Loader2, Save } from 'lucide-react';
import PhoneInput from '../../shared/PhoneInput';
import MoneyInput from '../../shared/MoneyInput';
import NumberInput from '../../shared/NumberInput';

export const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function ModuleHeader({ title, subtitle, onSave, saving, extraButtons }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
      <div>
        <h2 className="font-serif text-xl font-bold" style={{ color: '#030524' }}>{title}</h2>
        {subtitle && <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        {extraButtons}
        {onSave && (
          <button onClick={onSave} disabled={saving} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5" style={{ background: '#070B5F' }} data-testid="module-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        )}
      </div>
    </div>
  );
}

export function TabBtn({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3.5 py-2.5 text-[12px] font-semibold border-b-2 transition ${active ? '' : 'text-slate-500 hover:text-slate-700 border-transparent'}`}
      style={active ? { color: '#070B5F', borderColor: '#6BA82E' } : {}}
    >
      {children}
    </button>
  );
}

export function Fld({ label, value, onChange, type = 'text', kind, step, integer, placeholder, testId, max }) {
  let field;
  if (kind === 'phone') {
    field = <PhoneInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1 h-9 text-[13px]" data-testid={testId} />;
  } else if (kind === 'money') {
    field = <MoneyInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1 h-9 text-[13px]" data-testid={testId} />;
  } else if (kind === 'number' || type === 'number') {
    // Decimals are always accepted; pass `integer` explicitly for a count.
    field = <NumberInput integer={!!integer} value={value} onChange={onChange} placeholder={placeholder} className="mt-1 h-9 text-[13px]" data-testid={testId} />;
  } else {
    field = <Input type={type} step={step} max={max} placeholder={placeholder} value={value ?? ''}
      onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-[13px]" data-testid={testId} />;
  }
  return (
    <div>
      <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">{label}</Label>
      {field}
    </div>
  );
}

// `money` renders a MoneyInput (dollars, cents kept, '$1,234.50' mask);
// otherwise a plain NumberInput that accepts decimals (rates, months, FICO).
// Either way the caller still receives a string, as it did with the old numeric input.
export function NumField({ label, value, onChange, testId, readOnly, money = false }) {
  const handle = (n) => onChange && onChange(n == null ? '' : String(n));
  const cls = `mt-1 h-9 text-[13px] ${readOnly ? 'bg-slate-50' : ''}`;
  return (
    <div>
      <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">{label}</Label>
      {money
        ? <MoneyInput value={value} readOnly={readOnly} onChange={handle} className={cls} data-testid={testId} />
        : <NumberInput value={value} readOnly={readOnly} onChange={handle} className={cls} data-testid={testId} />}
    </div>
  );
}

export function SelectFld({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9 text-[13px]" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
